import { INode } from './interfaces';
import { ENDPOINT_DEVICE, INFRASTRUCTURE_DEVICES, MEASURING_DEVICES, INFRASTRUCTURE_DEVICE, UNKNOWN_DEVICES } from './Devices';

const NONOPERATIONAL_DEVICES = 'nonoperational_devices';

export const getNodeType = (node: INode): string => {
	const { DeviceRole, MainSensorType } = node;
	if (node.SystemVersion === 'W2' && !node.W2Operational) {
		return NONOPERATIONAL_DEVICES;
	}
	if (DeviceRole === ENDPOINT_DEVICE || MainSensorType !== 'none') {
		return MEASURING_DEVICES;
	}
	if ((Object.values(INFRASTRUCTURE_DEVICES) as string[]).includes(DeviceRole)) {
		return INFRASTRUCTURE_DEVICE;
	}
	return UNKNOWN_DEVICES;
};

export const computeDeviceLostMap = (nodes: INode[]): Record<number, number> => {
	const showWarningTime = 1000 * 60 * 60 * 24;
	const currentDate = Date.now();
	const map: Record<number, number> = {};
	for (const node of nodes) {
		const canMeasure =
			node.CanMeasure ||
			node.DeviceRole === 'retransmitter' ||
			(node.DeviceRole === 'access_device' && node.DeviceType !== 'UD-10.1.1');
		const isOperational = node.SystemVersion !== 'W2' || node.W2Operational === true;

		if (!canMeasure || !isOperational) {
			map[node.id] = 0;
		} else if (node.SystemVersion === 'W2') {
			const parsedDate = Date.parse(node.LatestSuccessfulMeasurementTimestamp);
			map[node.id] = Number(currentDate - parsedDate > showWarningTime && node.W2ConnectionStatus === 3);
		} else {
			const parsedDate = Date.parse(node.LatestPacketTimestamp);
			map[node.id] = Number(currentDate - parsedDate > showWarningTime);
		}
	}
	return map;
};

export const sortNodesForDisplay = (nodes: INode[], deviceLostMap: Record<number, number>): INode[] => {
	const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
	return [...nodes]
		.sort((a, b) => collator.compare(a.name, b.name))
		.sort((a, b) => a.deviceRoleSortable - b.deviceRoleSortable)
		.sort((a, b) => deviceLostMap[b.id] - deviceLostMap[a.id])
		.sort((a, b) => Number(b.deviceDamage) - Number(a.deviceDamage))
		.sort((a, b) => a.deviceThreatLevelSortable - b.deviceThreatLevelSortable);
};

export const sortNonoperationalNodesForDisplay = (nodes: INode[]): INode[] => {
	const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
	return [...nodes]
		.sort((a, b) => collator.compare(a.name, b.name))
		.sort((a, b) => a.deviceRoleSortable - b.deviceRoleSortable);
};

export const getOrderedGraphableNodes = (allNodes: INode[]): INode[] => {
	const deviceLostMap = computeDeviceLostMap(allNodes);
	const measuring = allNodes.filter((n) => getNodeType(n) === MEASURING_DEVICES);
	const infrastructure = allNodes.filter((n) => getNodeType(n) === INFRASTRUCTURE_DEVICE);
	return [
		...sortNodesForDisplay(measuring, deviceLostMap),
		...sortNodesForDisplay(infrastructure, deviceLostMap),
	];
};